import axios from "axios";
import { TokenResponse } from "@/types/Auth.types";

// Define base URLs
const BASE_URL = process.env.NEXT_PUBLIC_APP_URL || '';
const API_BASE_URL = BASE_URL + process.env.NEXT_PUBLIC_API_SUFFIX;
const SERVICE_PREFIX = process.env.NEXT_PUBLIC_SERVICE_PREFIX || '';
const API_VERSION = process.env.NEXT_PUBLIC_API_VERSION || 'v1';
// const env = process.env.NEXT_PUBLIC_ENV_SUFFIX

const ACCOUNT_SERVICE_PATH = `/${SERVICE_PREFIX}account-service/account/${API_VERSION}`;
const PRODUCT_SERVICE_PATH = `/product-service/api/${API_VERSION}`;
const AGGREGATED_SERVICE_PATH= `/agg-service/api/${API_VERSION}`; 
const VENDOR_SERVICE_PATH = `/vendor-service/api/${API_VERSION}`;
const VENDOR_GUEST_SERVICE_PATH = `/vendor-service/guest/api/${API_VERSION}`;
const ACCOUNT_SERVICE_ACCOUNT_PATH = `/${SERVICE_PREFIX}account-service/api/${API_VERSION}/`;
const GLOSSARY_SERVICE_PATH = `/glossary-service/api/${API_VERSION}`;
const PACKAGING_SERVICE= `/packaging-service/api/${API_VERSION}`;
const WORKFLOW_TRANSITION_SERVICE_PATH = `/pimcore-service/api/${API_VERSION}`;
const ONLOAD_SERVICE_PATH = `/${SERVICE_PREFIX}account-service/api/${API_VERSION}`;
const PIMCORE_SERVICE_PATH = `/pimcore-service/api/${API_VERSION}`;
const PIMCORE_GUEST_SERVICE_PATH = `/pimcore-service/guest/api/${API_VERSION}`;
const IAM_SERVICE_PATH = `/${SERVICE_PREFIX}iam-service/api/${API_VERSION}`;

/** Refresh access token this many milliseconds before `expiredOnMillis`. */
const TOKEN_REFRESH_BUFFER_MS = 10 * 60 * 1000;

function api(){
  let authenticated = false;
  const accountServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${ACCOUNT_SERVICE_PATH}`,
  });

  const productServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${PRODUCT_SERVICE_PATH}`,
  });
  const aggregatedServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${AGGREGATED_SERVICE_PATH}`
  })
  const vendorServiceAPI = axios.create({
    baseURL:`${API_BASE_URL}${VENDOR_SERVICE_PATH}` 
  })
  const VendorGuestServiceAPI = axios.create({
    baseURL:`${API_BASE_URL}${VENDOR_GUEST_SERVICE_PATH}` 
  })
  const accountServiceAccountAPI = axios.create({
    baseURL: `${API_BASE_URL}${ACCOUNT_SERVICE_ACCOUNT_PATH}`,
  });
  const glossaryServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${GLOSSARY_SERVICE_PATH}`,
  });
  const packagingServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${PACKAGING_SERVICE}`,
  });
  const workflowTransitionServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${WORKFLOW_TRANSITION_SERVICE_PATH}`,  
  });
  const onloadServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${ONLOAD_SERVICE_PATH}`,
  });
  const pimcoreServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${PIMCORE_SERVICE_PATH}`,
  });
  const iamServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${IAM_SERVICE_PATH}`,
  });
  const pimcoreGuestServiceAPI = axios.create({
    baseURL: `${API_BASE_URL}${PIMCORE_GUEST_SERVICE_PATH}`,
  });
  const Services = [
    accountServiceAPI,
    productServiceAPI,
    aggregatedServiceAPI,
    vendorServiceAPI,
    VendorGuestServiceAPI,
    accountServiceAccountAPI,
    glossaryServiceAPI,
    packagingServiceAPI,
    workflowTransitionServiceAPI,
    onloadServiceAPI,
    pimcoreServiceAPI,
    iamServiceAPI,
    pimcoreGuestServiceAPI
  ];

  // Axios defaults withCredentials=false; set on the instance so cookies always ride (session/refresh cookies on API host).
  Services.forEach((svc) => {
    svc.defaults.withCredentials = true;
  });

  // Token store in a closure (not exposed globally)
  const store = (() => {
    let token: string | null = null; // Private variable to store the token
    let expiredOnMillis: number | null = null;
    let user: TokenResponse['iAm'] | null = null;
    const setToken = (newToken: string) => {
      token = newToken; // Set the token in the closure
      authenticated = true;
    };
    const getToken = () => token; // Access the token from the closure
    const setExpiredOnMillis = (ms: number | null) => {
      expiredOnMillis = ms;
    };
    const getExpiredOnMillis = () => expiredOnMillis;
    const clearToken = () => {
      token = null; // Clear the token (e.g., on logout)
      expiredOnMillis = null;
      authenticated = false;
    };
    const setUserData = (userData: TokenResponse['iAm']) => {
      user = userData; // Set the token in the closure
      };
    const getUserData = (): TokenResponse['iAm'] | null => user;
    return {
      setToken,
      getToken,
      clearToken,
      setExpiredOnMillis,
      getExpiredOnMillis,
      setUserData,
      getUserData,
    };
  })();

  const redirectSignIn = () => {
    if (typeof window !== "undefined") {
      let redirectUrl =
        API_BASE_URL +
        ACCOUNT_SERVICE_PATH +
        "/signIn" +
        "?returnUrl=" +
        encodeURIComponent(window.location.href);
      window.location.href = redirectUrl;
    }
    
  };
  /**
   * Full token load (localStorage or GET /token). Does not use getTokenFetchPromise — call via getTokenFetchPromise only.
   * Waits for any in-flight force-refresh first so parallel callers share one network round-trip.
   */
  const fetchTokenInternal = async (options?: {
    skipSavedUserInfo?: boolean;
  }): Promise<string> => {
    try {
      const inFlightRefresh = refreshTokenPromise;
      if (inFlightRefresh) {
        try {
          await inFlightRefresh;
        } catch {
          /* refresh failed; still attempt normal token path */
        }
      }

      const tokenAfterRefresh = store.getToken();
      const expAfterRefresh = store.getExpiredOnMillis();
      if (
        tokenAfterRefresh &&
        expAfterRefresh != null &&
        Date.now() < expAfterRefresh - TOKEN_REFRESH_BUFFER_MS
      ) {
        return tokenAfterRefresh;
      }

      const savedUserInfo = options?.skipSavedUserInfo ? null : getSavedUserInfo();
      let response: { status: number; data: TokenResponse };
      if(savedUserInfo){
        response = savedUserInfo;
      }else{
        response = await accountServiceAPI.get(`/token`, { withCredentials: true });
      }

      if(response.status !== 200){
        redirectSignIn();
      }

      // Do not use here optional operator as it will not work in IE, it is replacing response with its data 
      if (response && response.data && response.data.token) {
      
        if((new Date()).getTime() > response.data.expiredOnMillis){
          throw new Error("Token expired");
        }
        if(!response.data.iAm){
          const email = response.data.user?.email || '';
          window.location.href= `/userNotFound?${new URLSearchParams({email}).toString()}`; 
        }
        store.setToken(response.data.token);
        store.setExpiredOnMillis(response.data.expiredOnMillis);
        store.setUserData(response.data.iAm);

        Services.forEach(service => {
          // Set the Authorization header for all requests
          service.defaults.headers.common["Authorization"] = `Bearer ${store.getToken()}`;
        });

        return response.data.token; // Adjust to the response structure of your refresh token API
      } else {
        throw new Error("Token not found in response");
      }
    } catch (error) {
      console.error("Error fetching token:", error);
      redirectSignIn();
      throw error; // Re-throw the error to maintain the Promise<string> return type
    }
  };

  const getSavedUserInfo = () => {
    if (typeof window === "undefined") return null;
    const userInfo = localStorage.getItem("userInfo");
    if (!userInfo) {
      return null;
    }
    const parsedUserInfo = JSON.parse(userInfo);
    if((new Date()).getTime() > parsedUserInfo.expiredOnMillis){
      window.location.href = `/linkExpired`;
     return null;   
    }
    return {
      status:200,
      data:parsedUserInfo
    };
  };

  // Single in-flight full token fetch: layout, interceptors, and 401 retries share one promise (no duplicate GET /token).
  let tokenFetchPromise: Promise<string> | null = null;
  const getTokenFetchPromise = (): Promise<string> => {
    if (tokenFetchPromise) {
      return tokenFetchPromise;
    }
    tokenFetchPromise = fetchTokenInternal().finally(() => {
      tokenFetchPromise = null;
    });
    return tokenFetchPromise;
  };

  /** Same as getTokenFetchPromise — use so direct callers (e.g. layout) dedupe with axios interceptors. */
  const fetchToken = (): Promise<string> => getTokenFetchPromise();

  /** Merge new token and expiry into persisted session without touching iAm / user. */
  const persistTokenFieldsToLocalStorage = (newToken: string, newExpiredOnMillis: number) => {
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem("userInfo");
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      parsed.token = newToken;
      parsed.expiredOnMillis = newExpiredOnMillis;
      localStorage.setItem("userInfo", JSON.stringify(parsed));
    } catch {
      /* ignore corrupt storage */
    }
  };

  // Single in-flight force refresh; parallel requests await this (one GET /token?forceRefreshToken=true).
  let refreshTokenPromise: Promise<void> | null = null;

  /** Force refresh from server; updates token + expiry only (not iAm / user in memory or storage). */
  const refreshAccessTokenOnly = async (): Promise<void> => {
    try {
      const response = await accountServiceAPI.get<TokenResponse>(
        "/token?forceRefreshToken=true",
        { withCredentials: true }
      );

      if (response.status !== 200 || !response.data?.token) {
        redirectSignIn();
        throw new Error("Token refresh failed");
      }

      store.setToken(response.data.token);
      store.setExpiredOnMillis(response.data.expiredOnMillis);
      Services.forEach((service) => {
        service.defaults.headers.common["Authorization"] = `Bearer ${store.getToken()}`;
      });
      persistTokenFieldsToLocalStorage(response.data.token, response.data.expiredOnMillis);
    } catch (error) {
      // Axios rejects on 4xx/5xx — the `if (response.status ...)` block never runs for those;
      // execution jumps here instead, so redirect must happen in catch as well.
      redirectSignIn();
      throw error;
    }
  };

  const getRefreshTokenPromise = (): Promise<void> => {
    if (refreshTokenPromise) {
      return refreshTokenPromise;
    }
    // Assign synchronously before the first await inside refreshAccessTokenOnly so concurrent interceptors share one flight.
    refreshTokenPromise = refreshAccessTokenOnly().finally(() => {
      refreshTokenPromise = null;
    });
    return refreshTokenPromise;
  };

  /**
   * If (expiredOnMillis - 10m) - now <= 0, refresh token only.
   * Uses a single in-flight refresh for concurrent requests.
   */
  const ensureFreshAccessTokenIfNeeded = async (): Promise<void> => {
    if (typeof window === "undefined") return;
    let exp = store.getExpiredOnMillis();
    if (exp == null) {
      const saved = getSavedUserInfo();
      if (saved?.data?.expiredOnMillis != null) {
        exp = saved.data.expiredOnMillis;
        store.setExpiredOnMillis(exp);
      }
    }
    if (exp == null) return;
    const millisUntilRefreshThreshold = exp - TOKEN_REFRESH_BUFFER_MS - Date.now();
    if (millisUntilRefreshThreshold > 0) return;
    try {
      await getRefreshTokenPromise();
    } catch (e) {
      console.error("Proactive token refresh failed:", e);
      redirectSignIn();
      throw e;
    }
  };

  const isTokenEndpointRequest = (config: any) => {
    const url = config?.url ?? "";
    const baseURL = config?.baseURL ?? "";
    const pathOnly = String(url).split("?")[0];
    const combined = `${baseURL}/${pathOnly}`.replace(/\/+/g, "/");
    return pathOnly === "/token" || combined.endsWith("/token");
  };

  /**
   * Wait for access-token refresh if another request already started it.
   * Callers that skip ensureFresh (e.g. exp unknown) must still not send stale Authorization while refresh is in flight.
   */
  const awaitInFlightTokenRefresh = async (): Promise<void> => {
    const inFlight = refreshTokenPromise;
    if (!inFlight) return;
    try {
      await inFlight;
    } catch {
      /* caller continues; may 401 and use response interceptor */
    }
  };

  /** Shared by all 401 retries: force-refresh first (deduped), then network token without localStorage cache. */
  let token401RecoveryPromise: Promise<string> | null = null;
  const getTokenFor401Retry = (): Promise<string> => {
    if (token401RecoveryPromise) {
      return token401RecoveryPromise;
    }
    token401RecoveryPromise = (async (): Promise<string> => {
      try {
        await getRefreshTokenPromise();
      } catch {
        /* continue to fetch without cache */
      }
      const t = store.getToken();
      const exp = store.getExpiredOnMillis();
      if (t && exp != null && Date.now() < exp) {
        return t;
      }
      return fetchTokenInternal({ skipSavedUserInfo: true });
    })().finally(() => {
      token401RecoveryPromise = null;
    });
    return token401RecoveryPromise;
  };

  const requestInterceptor = async (config: any) => {
    config.withCredentials = true;

    // Never wait for token when this request is the /token call itself (avoids deadlock)
    if (isTokenEndpointRequest(config)) {
      return config;
    }

    // Serialize on in-flight refresh so parallel calls don't use stale bearer while GET .../token?forceRefreshToken=true runs.
    await awaitInFlightTokenRefresh();

    // For all other requests: ensure we have a token before sending so no request goes out with no token (avoids duplicate 401s)
    let token = store.getToken();
    if (!token) {
      try {
        token = await getTokenFetchPromise();
      } catch (e) {
        return Promise.reject(e);
      }
    }
    if (token) {
      try {
        await ensureFreshAccessTokenIfNeeded();
        token = store.getToken();
      } catch (e) {
        return Promise.reject(e);
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  };

  const requestError = async (error: any) => {
    return Promise.reject(error);
  };

  // Error interceptor to handle expired token and retry the request
  const responseInterceptor = (response: any) => {
    // If the response is successful, just return it
    return response;
  };

  const responseError = async (error: any) => {
    const originalRequest = error.config;

    // Do not retry when the failing request is the token endpoint (avoids refresh loop)
    if (isTokenEndpointRequest(originalRequest)) {
      return Promise.reject(error);
    }

    if (
      error.response &&
      error.response.status === 401 &&
      error.response.status !== 403 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await getTokenFor401Retry();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        const serviceInstance =
          Services.find((s) => s.defaults.baseURL === originalRequest.baseURL) ?? accountServiceAPI;
        return serviceInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", {
          "refreshError (token refresh error)": refreshError,
          "url (request url)": originalRequest?.url,
          "baseURL (request base url)": originalRequest?.baseURL,
          "method (request method)": originalRequest?.method,
        });
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  };


Services.forEach(service => {
  // Add request and response interceptors
  service.interceptors.request.use(requestInterceptor, requestError);
  // Add response interceptors
  service.interceptors.response.use(responseInterceptor, responseError);
});

    /**
   * Keepalive API - for requests that need to complete even during browser unload
   * Uses fetch with keepalive flag instead of axios
   */
  const keepaliveAPI = {
    delete: (serviceInstance: any, url: string) => {
      const baseURL = serviceInstance.defaults.baseURL;
      const authHeader = serviceInstance.defaults.headers.common['Authorization'];
      const fullUrl = `${baseURL}${url}`;
      
      return fetch(fullUrl, {
        method: 'DELETE',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader && { 'Authorization': authHeader as string }),
        },
        credentials: 'include',
      }).catch(err => {
        console.error('Keepalive DELETE error:', err);
      });
    },
  };


  const { getUserData, getToken } = store;
  return {
    accountServiceAPI,
    productServiceAPI,
    aggregatedServiceAPI, 
    vendorServiceAPI,
    VendorGuestServiceAPI,
    accountServiceAccountAPI,
    glossaryServiceAPI,
    packagingServiceAPI,
    workflowTransitionServiceAPI,
    onloadServiceAPI,
    pimcoreServiceAPI,
    iamServiceAPI,
    pimcoreGuestServiceAPI,
    fetchToken,
    authenticated, 
    getToken,
    getUserData,
    redirectSignIn,
    keepaliveAPI
  };
}

const apiInstance = api();

export default apiInstance;